import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import EnterActiveReviewModeGridCell from './enter-active-review-mode-grid-cell';
import SMSelectedReviewSection from './selected-review-section';
import Card from '@components-clean/core/card';
import CoreSplitter from '@components-clean/core/core-splitter';
import ReasonForChangeDialog from '@components-clean/core/dialogs/reason-for-change-dialog';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import Grid from '@components-clean/core/grid-fe';
import { DateCell, YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { EditReviewDto, ExistingReview, SubjectMonitoringReviewDto } from '@interfaces-clean/reviews.interface';
import { MonitoringToolkitContext } from '@pages-clean/central-monitoring/monitoring-toolkit/context';
import AddSubjectMonitoringReviewDialog from '@pages-clean/subject-monitoring/components/dialogs/add-subject-monitoring-review-dialog';
import { useSMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import EditReviewDetails from '@pages-clean/subject-monitoring/components/monitoring-toolkit/subject-monitoring-review-grid/edit-review-details';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useAuthorization, useBoolean } from '@hooks/index';
import { SplitterOrientation } from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { GET_SM_STUDY_MEMBERS } from '@queries/studies';
import { ReviewPlanAPI } from '@apis/bff-api/study/review-plans';
import { SubjectMonitoringStudyMemberApi } from '@apis/bff-api/subject-monitoring/subject-monitoring-study-member-api';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

interface ReviewGridProps {
    canEdit: boolean;
    studyRef: string;
    timestamp?: string;
    enterReviewMode: (reviewRecordId: number, sessionId: string) => void;
}

interface ITableInfo {
    columns: GridColumnProps[];
    data: { [key: string]: unknown }[];
}

const SubjectMonitoringReviewGrid = ({ canEdit, studyRef, timestamp, enterReviewMode }: ReviewGridProps) => {
    const { t: tCommon } = useTranslation();
    const { t: mtkTranslation }: { t: (key: string) => string } = useTranslation('sm', {
        keyPrefix: 'monitoringToolkit',
    });
    const { studyNumber } = useContext(MonitoringToolkitContext);
    const smActiveReviewContext = useSMActiveReviewContext();
    const inReviewMode = smActiveReviewContext.state.reviewRecordId != null;

    const user = useAuthorization();
    const userId = user?.id;

    // Get list of all sm study members for current study
    const getSmStudyMembersQuery = useQuery(GET_SM_STUDY_MEMBERS, { variables: { studyId: studyRef, timestamp } });
    // Try to get id of current user's study membership, if they are a member (this is different from their user id)
    const smStudyMemberId = useMemo(() => {
        if (userId == null) {
            return undefined;
        }

        // Try to find sm study member with userId of current user
        const matchedUser = getSmStudyMembersQuery.data?.smStudyMembers?.find((m) => m?.userId === userId);

        // Return the id of the membership record
        return matchedUser?.id as string | undefined;
    }, [userId, getSmStudyMembersQuery.data]);

    const showAddDialog = useBoolean(false);
    const showEditDialog = useBoolean(false);
    const showDeleteDialog = useBoolean(false);
    const [reviewToDelete, setReviewToDelete] = useState<SubjectMonitoringReviewDto | undefined>(undefined);

    const { data, isMutating, trigger: loadReviews } = SubjectMonitoringReviewAPI.useGetAllReviews(studyRef, timestamp);
    const { trigger: deleteReview } = SubjectMonitoringReviewAPI.useDeleteReview(studyRef, reviewToDelete?.recordId);

    const getSmStudyMembersQueryData = SubjectMonitoringStudyMemberApi.useGetAllStudyMembers(studyRef, true);

    const [selectedItem, setSelectedItem] = useState<SubjectMonitoringReviewDto | undefined>(undefined);
    const [datasetDates, setDatasetDates] = useState<string[]>([]);
    const [existingReviews, setExistingReviews] = useState<ExistingReview[]>([]);
    const { TooltipCell } = useGridCustomCell();

    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit' });

    const { data: reviewPlans } = ReviewPlanAPI.useGetAll(studyRef);
    const reviewTypeNames = useMemo(
        () => (reviewPlans ?? []).map((plan) => plan.reviewTypeName!).sort((a, b) => a.localeCompare(b)),
        [reviewPlans],
    );
    const tooltipCellTwoLine = TooltipCell({
        isCsv: false,
        maxLines: 2,
    });

    const updateDatasetDates = (reloadedData: SubjectMonitoringReviewDto[]) => {
        const dates = reloadedData
            .map((review) => review.datasetDate!)
            .filter((value, index, array) => array.indexOf(value) === index) // unique
            .sort((a, b) => (new Date(a) > new Date(b) ? -1 : 1)); // descending
        setDatasetDates(dates);
    };

    /**
     * Sets the existing review data state const, to be used for the Add modal dialog.
     *
     * Called on mount of the component and when a new review has been added.
     * @param loadedReviews The reviews to set
     */
    const setExistingReviewsData = (loadedReviews: SubjectMonitoringReviewDto[]) => {
        const reviews: ExistingReview[] = loadedReviews.map((reviewData) => {
            return {
                id: reviewData.id!,
                title: reviewData.title!,
                datasetId: reviewData.datasetId!,
                type: reviewData.type!,
            };
        });
        setExistingReviews(reviews);
    };

    /**
     * Sets the selected review as the one with the given ID if it exists.
     * @param loadedReviews The reviews to search
     * @param addedReviewId The ID of the review to set as the selected
     */
    const setSelectedReview = (loadedReviews: SubjectMonitoringReviewDto[], addedReviewId?: number) => {
        if (addedReviewId) {
            const addedRow = loadedReviews.filter((newData) => newData.recordId === addedReviewId);
            if (addedRow.length) {
                setSelectedItem(addedRow[0]);
            }
        }
    };

    useEffect(() => {
        if (loadReviews) {
            loadReviews()
                .then((loadedData) => {
                    setExistingReviewsData(loadedData);
                    updateDatasetDates(loadedData);
                })
                .catch((err) => window.console.log(err));
        }
    }, [loadReviews]);

    const tableInfo = useMemo(() => {
        if (!data) return undefined;

        const info: ITableInfo = {
            columns: [
                {
                    id: 'open-review',
                    title: t('manageReviewsGrid.columnsTitles.review'),
                    minWidth: 142,
                    width: 'auto',
                    lockedByDefault: true,
                    resizable: false,
                    filterable: false,
                    cells: {
                        data: (cellProps) => (
                            <EnterActiveReviewModeGridCell
                                {...cellProps}
                                smStudyMemberId={smStudyMemberId}
                                studyRef={studyRef}
                                canEdit={canEdit}
                                enterReviewMode={enterReviewMode}
                            />
                        ),
                    },
                },
                {
                    id: 'type',
                    field: 'type',
                    title: t('manageReviewsGrid.columnsTitles.type'),
                    minWidth: 140,
                    maxLength: 30,
                    cells: { data: tooltipCellTwoLine },
                    lovData: reviewTypeNames,
                    lockedByDefault: true,
                },
                {
                    id: 'title',
                    field: 'title',
                    title: t('manageReviewsGrid.columnsTitles.title'),
                    minWidth: 140,
                    maxLength: 30,
                    cells: { data: tooltipCellTwoLine },
                    lockedByDefault: true,
                },
                {
                    id: 'reviewed',
                    field: 'reviewed',
                    title: t('manageReviewsGrid.columnsTitles.reviewed'),
                    minWidth: 100,
                    cells: { data: YesNoCell },
                    filter: 'boolean',
                },
                {
                    id: 'datasetDate',
                    field: 'datasetDate',
                    title: t('manageReviewsGrid.columnsTitles.datasetDate'),
                    minWidth: 140,
                    lovData: datasetDates,
                },
                {
                    id: 'reviewers',
                    field: 'reviewers',
                    title: t('manageReviewsGrid.columnsTitles.reviewers'),
                    minWidth: 140,
                    cells: { data: tooltipCellTwoLine },
                },
                {
                    id: 'reviewSummary',
                    field: 'reviewSummary',
                    title: t('manageReviewsGrid.columnsTitles.reviewSummary'),
                    minWidth: 140,
                    cells: { data: tooltipCellTwoLine },
                },
                {
                    id: 'reviewStartDate',
                    field: 'reviewStartDate',
                    title: t('manageReviewsGrid.columnsTitles.reviewStartDate'),
                    minWidth: 120,
                    cells: { data: DateCell },
                    cellName: 'DateCell',
                    filter: 'date',
                },
                {
                    id: 'reviewEndDate',
                    field: 'reviewEndDate',
                    title: t('manageReviewsGrid.columnsTitles.reviewEndDate'),
                    minWidth: 120,
                    cells: { data: DateCell },
                    cellName: 'DateCell',
                    filter: 'date',
                },
                {
                    id: 'createdByName',
                    field: 'createdByName',
                    title: t('manageReviewsGrid.columnsTitles.createdBy'),
                    minWidth: 140,
                    cells: { data: tooltipCellTwoLine },
                },
                {
                    id: 'created',
                    field: 'createdDate',
                    title: t('manageReviewsGrid.columnsTitles.createdOn'),
                    minWidth: 120,
                    cells: { data: DateCell },
                    cellName: 'DateCell',
                    filter: 'date',
                },
                {
                    id: 'updatedByName',
                    field: 'updatedByName',
                    title: t('manageReviewsGrid.columnsTitles.updatedBy'),
                    minWidth: 140,
                    cells: { data: tooltipCellTwoLine },
                },
                {
                    id: 'updatedDate',
                    field: 'updatedDate',
                    title: t('manageReviewsGrid.columnsTitles.updatedOn'),
                    minWidth: 120,
                    cells: { data: DateCell },
                    cellName: 'DateCell',
                    filter: 'date',
                },
            ],
            data,
        };

        return info;
    }, [
        data,
        datasetDates,
        reviewTypeNames,
        t,
        tooltipCellTwoLine,
        canEdit,
        smStudyMemberId,
        studyRef,
        enterReviewMode,
    ]);

    let menuActions: RowMenuAction[] | undefined;

    if (canEdit) {
        menuActions = [
            {
                id: 1,
                label: t('manageReviewsGrid.actions.editLabel'),
                icon: ['far', 'pen-to-square'],
                onClick: (dataItem: unknown) => {
                    setSelectedItem(dataItem as SubjectMonitoringReviewDto);
                    showEditDialog.setTrue();
                },
                disabled: inReviewMode,
            },
            {
                id: 2,
                label: t('manageReviewsGrid.actions.deleteLabel'),
                icon: ['fas', 'trash-xmark'],
                colorProp: 'Red',
                onClick: (dataItem: unknown) => {
                    setReviewToDelete(dataItem as SubjectMonitoringReviewDto);
                    showDeleteDialog.setTrue();
                },
                disabled: inReviewMode,
                rowDisabled: (dataItem: unknown) => (dataItem as SubjectMonitoringReviewDto).reviewStartDate != null,
            },
        ];
    }

    const addCallback = () => {
        showAddDialog.setTrue();
    };

    const onAddClose = () => {
        showAddDialog.setFalse();
    };

    const onAddSubmit = async (_addedId?: number, addedRecordId?: number) => {
        showAddDialog.setFalse();
        loadReviews()
            .then((reloadedData) => {
                setExistingReviewsData(reloadedData);
                setSelectedReview(reloadedData, addedRecordId);
                updateDatasetDates(reloadedData);
            })
            .catch((err) => window.console.log(err));
    };

    const closeDeleteDialog = () => {
        showDeleteDialog.setFalse();
        setReviewToDelete(undefined);
    };

    const onDeleteConfirm = async (reason: string) => {
        if (reviewToDelete?.recordId == null) {
            return;
        }

        try {
            await deleteReview({ reasonForChange: reason });

            if (selectedItem?.recordId === reviewToDelete.recordId) {
                setSelectedItem(undefined);
            }
            closeDeleteDialog();
            ShowSuccess(tCommon('actionsSuccessfulToast'));

            const reloadedData = await loadReviews();
            setExistingReviewsData(reloadedData);
            updateDatasetDates(reloadedData);
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const editReviewDto = useMemo<EditReviewDto | undefined>(() => {
        if (!selectedItem) return undefined;
        return {
            id: selectedItem.id,
            type: selectedItem.type,
            title: selectedItem.title,
            reviewed: selectedItem.reviewed,
            datasetDate: selectedItem.datasetDate,
            reviewers: selectedItem.reviewerIds?.map((ref) => ({ ref })) ?? null,
            reviewSummary: selectedItem.reviewSummary,
            reviewStartDate: selectedItem.reviewStartDate,
            reviewEndDate: selectedItem.reviewEndDate,
            createdDate: selectedItem.createdDate,
            createdByName: selectedItem.createdByName,
            updatedDate: selectedItem.updatedDate,
            updatedByName: selectedItem.updatedByName,
            recordId: selectedItem.recordId,
            datasetId: selectedItem.datasetId,
        } satisfies EditReviewDto;
    }, [selectedItem]);

    const isLoading = isMutating || getSmStudyMembersQuery.loading;
    // Only unmount the grid for the initial load or while study members are loading.
    // Mutations (add/edit/delete) use the grid overlay so the grid stays mounted and
    // virtual scroll position is preserved - otherwise remounting at skip=0 causes the
    // post-add scroll-to-row to bounce back to top.
    const shouldShowLoadingCard = !tableInfo || getSmStudyMembersQuery.loading;

    if (shouldShowLoadingCard) {
        return (
            <Card className="placeholder-card" data-testid="grid" title={t('manageReviewsGrid.title')}>
                {isLoading ? <Loading /> : <InfoField message={t('emptyMessage')} type="Info" layout="center" />}
            </Card>
        );
    }

    return (
        <>
            {showAddDialog.value && (
                <AddSubjectMonitoringReviewDialog
                    studyRef={studyRef}
                    onClose={onAddClose}
                    onSubmit={onAddSubmit}
                    reviews={existingReviews}
                />
            )}
            {showEditDialog.value && getSmStudyMembersQueryData.data && editReviewDto && (
                <EditReviewDetails
                    studyRef={studyRef}
                    review={editReviewDto}
                    smStudyMembers={getSmStudyMembersQueryData.data}
                    closeFn={showEditDialog.setFalse}
                    saveFn={async () => {
                        showEditDialog.setFalse();
                        await onAddSubmit(undefined, editReviewDto.recordId!);
                    }}
                    existingReviews={existingReviews}
                />
            )}
            {showDeleteDialog.value && reviewToDelete && (
                <ReasonForChangeDialog
                    title={t('manageReviewsGrid.deleteReviewDialog.title')}
                    reasonFieldLabel={t('manageReviewsGrid.deleteReviewDialog.reasonForChange')}
                    maxLength={3000}
                    onCancel={closeDeleteDialog}
                    onSubmit={onDeleteConfirm}
                />
            )}
            <CoreSplitter
                className="!k-h-full"
                data-testid="manage-reports-splitter"
                orientation={SplitterOrientation.vertical}
            >
                <div data-testid="subject-observations-pane" className="k-h-full">
                    <Grid
                        dataItemKey="recordId"
                        title={t('manageReviewsGrid.title')}
                        maxHeight="auto"
                        testDataId="all-tags-grid"
                        data={tableInfo.data}
                        columns={tableInfo.columns}
                        rowMenuActions={menuActions}
                        loading={isLoading}
                        useLoadingOverlay
                        rowReorder={false}
                        addCallback={addCallback}
                        canEdit={canEdit}
                        showAddIcon={canEdit}
                        selectable={{ enabled: true, mode: 'single' }}
                        defaultSelectedItem={selectedItem}
                        exportFilename={`${studyNumber}_Subject Monitoring Reviews_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`}
                        service={AuditServiceType.SUBJECT_MONITORING}
                        area={AuditApplicationArea.SUBJECT_MONITORING_REVIEW}
                        dataType={AuditDataType.EXCEL_EXPORT}
                        scrollable="virtual"
                        onSelectionChange={(e: GridSelectionChangeEvent) => {
                            // Cannot select multiple items, so startDataItem is fine
                            const record = e.startDataItem as SubjectMonitoringReviewDto;
                            if (!record) {
                                return;
                            }
                            setSelectedItem(record);
                        }}
                    />
                </div>
                <div data-testid="subject-observations-pane" className="k-h-full">
                    {selectedItem?.recordId != null ? (
                        <SMSelectedReviewSection studyRef={studyRef} reviewRecordId={selectedItem.recordId} />
                    ) : (
                        <InfoField
                            type="Info"
                            message={mtkTranslation('reviewActivityLogGrid.noRecordSelected')}
                            layout="center"
                        />
                    )}
                </div>
            </CoreSplitter>
        </>
    );
};

export default SubjectMonitoringReviewGrid;
